import type { JSX } from "react";
import { cx } from "./classNames";
import { Loading } from "./Loading";
import styles from "./ui.module.css";

/**
 * Shape of the placeholder.
 *
 * - `lines`: lines of text, for example a summary or a short list.
 * - `list`: list items with a title line and a detail line each.
 * - `card`: one card with a heading and lines, for example balances.
 */
export type SkeletonShape = "lines" | "list" | "card";

/** Properties of {@link Skeleton}. */
export interface SkeletonProps {
  /** Shape. */
  readonly shape: SkeletonShape;
  /** Number of lines (`lines`, `card`) or items (`list`); at least 1, default 3. */
  readonly count?: number | undefined;
}

/**
 * Placeholder of loading content with roughly the final height, so the content below does not jump
 * when the data arrives (S-1, UX-117). The bars are hidden from assistive technology; the loading
 * state is announced by a visually hidden {@link Loading}. The shimmer only runs without
 * `prefers-reduced-motion`.
 *
 * @param props - Component properties.
 * @returns The placeholder.
 */
export function Skeleton(props: SkeletonProps): JSX.Element {
  const count = Math.max(1, Math.floor(props.count ?? 3));
  const indexes = Array.from({ length: count }, (_, index) => index);
  let bars: JSX.Element;

  if (props.shape === "list") {
    bars = (
      <div className={styles["stack"]}>
        {indexes.map((index) => (
          <div key={index} className={styles["skeletonItem"]}>
            <span className={cx(styles["skeletonBar"], styles["skeletonTitle"])} />
            <span className={styles["skeletonBar"]} />
          </div>
        ))}
      </div>
    );
  } else if (props.shape === "card") {
    bars = (
      <div className={cx(styles["card"], styles["skeletonCard"])}>
        <span className={cx(styles["skeletonBar"], styles["skeletonHeading"])} />
        {indexes.map((index) => (
          <span key={index} className={styles["skeletonBar"]} />
        ))}
      </div>
    );
  } else {
    bars = (
      <div className={styles["skeletonLines"]}>
        {indexes.map((index) => (
          <span key={index} className={styles["skeletonBar"]} />
        ))}
      </div>
    );
  }

  const skeleton = (
    <div className={styles["skeleton"]} data-shape={props.shape}>
      <Loading visuallyHidden />
      <div aria-hidden="true">{bars}</div>
    </div>
  );

  return skeleton;
}
